// Same-origin by default: the Express server in backend/server.js serves both
// the API and this React build, so a relative '/api' works on the LAN origin
// (http://<ip>:5002) and on the public HTTPS host alike. An absolute http://
// URL breaks in production - the browser blocks it as mixed content and the
// CSP connect-src 'self' rejects it, which surfaces as "Failed to fetch".
// Override only for a split deployment, and add that origin to CSP_CONNECT_SRC
// on the API side.
const BASE = process.env.REACT_APP_API_BASE || '/api';

function getToken() {
  return sessionStorage.getItem('mcq_token');
}

async function request(path, { method = 'GET', body, headers = {}, raw = false } = {}) {
  const token = getToken();
  const opts = {
    method,
    headers: {
      ...(body && !(body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
    },
  };
  if (body) opts.body = body instanceof FormData ? body : JSON.stringify(body);

  const res = await fetch(`${BASE}${path}`, opts);

  if (res.status === 401) {
    sessionStorage.removeItem('mcq_token');
    sessionStorage.removeItem('mcq_user');
    if (!path.includes('/auth/login')) {
      window.location.href = '/login';
    }
  }

  if (raw) return res;

  const contentType = res.headers.get('content-type') || '';
  const data = contentType.includes('application/json') ? await res.json() : await res.text();

  if (!res.ok) {
    const message = (data && data.error) || (typeof data === 'string' ? data : 'Request failed');
    const err = new Error(message);
    if (data && data.details) err.details = data.details;
    throw err;
  }
  return data;
}

// Authenticated file download. A plain <a href> can't send the Bearer token,
// so protected downloads (xlsx / pdf) must be fetched with the auth header and
// saved as a blob.
async function download(path, fallbackName = 'download') {
  const token = getToken();
  const res = await fetch(`${BASE}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (res.status === 401) {
    sessionStorage.removeItem('mcq_token');
    sessionStorage.removeItem('mcq_user');
    window.location.href = '/login';
    return;
  }
  if (!res.ok) {
    let message = 'Download failed';
    try { const j = await res.json(); message = j.error || message; } catch (e) { /* non-JSON error body */ }
    throw new Error(message);
  }

  const cd = res.headers.get('content-disposition') || '';
  const match = cd.match(/filename\*?=(?:UTF-8''|")?([^;"']+)/i);
  const filename = match ? decodeURIComponent(match[1]) : fallbackName;

  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export const api = {
  get: (path) => request(path),
  post: (path, body) => request(path, { method: 'POST', body }),
  put: (path, body) => request(path, { method: 'PUT', body }),
  del: (path) => request(path, { method: 'DELETE' }),
  raw: (path, opts) => request(path, { ...opts, raw: true }),
  download,
  BASE,
  getToken,
};
